import { AddToCartMethod, getAnalyticsItemType } from '@mb/lib';
import { ResponseStatusEnum } from '@productSearch/types';
import { StatusEnum as SnackbarStatusEnum } from 'components/components/MBSnackbar';

import { useLabels } from './useLabels';
import { sendAnalytics } from '../api/analytics';
import { addToCart } from '../api/cart';
import { getAnalyticsItemCategoriesByTaxonomy } from '../helpers/analytics';
import controlRulesById from '../RecommendationsModalContainer/controlRulesById';
import {
  ModalProps,
  RecommendedItemType,
} from '../types/recommendationsModal.types';
import { pushGtmDataLayer } from '../utils';
import { BANNER_SUBTITLE_DEFAULT_TEXT } from './useRecommendationsModalAnalytics';

type UseRecommendationModalHandlersParams = {
  modalProps: ModalProps;
  recommendationsIds: (string | number)[];
  xHeader: string | null;
  closeTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;

  textLabels: ReturnType<typeof useLabels>['textLabels'];
  setModalProps: React.Dispatch<React.SetStateAction<ModalProps>>;
  toggleSnackbarInfo: (props: {
    isOpen: boolean;
    otherProps: { message: string; status: SnackbarStatusEnum };
  }) => void;
};

export function useRecommendationModalHandlers({
  modalProps,
  recommendationsIds,
  xHeader,
  closeTimeoutRef,
  textLabels,
  setModalProps,
  toggleSnackbarInfo,
}: UseRecommendationModalHandlersParams) {
  const handleAddToCart = async (item: RecommendedItemType) => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
    }

    const newRecommendedItems = modalProps.recommendedItems.map((newItem) => ({
      ...newItem,
      isCartLoading:
        String(newItem.id) === String(item.id) ? true : newItem.isCartLoading,
    }));

    setModalProps((prevProps) => ({
      ...prevProps,
      recommendedItems: newRecommendedItems,
    }));

    try {
      if (modalProps.bagProject == null) {
        throw new Error(textLabels.selectProjectMessage);
      }

      const data = await addToCart([item], modalProps.bagProject);
      const isServerMessage = data?.message?.length > 0;

      try {
        await sendAnalytics(
          {
            id: [String(item.id)],
            type: 'cart',
            impressions: modalProps.recommendedItems?.map((item) =>
              String(item.id),
            ),
          },
          xHeader,
        );
      } catch (error) {
        console.error('Cannot send v2 analytics', error);
      }

      if (data?.status === ResponseStatusEnum.FAILED) {
        toggleSnackbarInfo({
          isOpen: true,
          otherProps: {
            message: data?.message ?? 'Some error occurred',
            status: SnackbarStatusEnum.DANGER,
          },
        });
      } else {
        if (isServerMessage) {
          window.dispatchEvent(
            new CustomEvent('show-control-rules-modal', {
              detail: {
                data: data,
              },
            }),
          );
        } else {
          // If something was added to cart
          if (!isServerMessage) {
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('reload-customer-section-data'),
              );
            }, 500);
          }
        }
      }

      if (data?.status === 'success' && !isServerMessage) {
        setModalProps((prevProps) => {
          const completedRecommendedItems = prevProps.recommendedItems.map(
            (newItem) => ({
              ...newItem,
              isCartLoading:
                newItem.id === item.id ? false : newItem.isCartLoading,
              isAddedToCart:
                newItem.id === item.id ? true : newItem.isAddedToCart,
            }),
          );

          return {
            ...prevProps,
            recommendedItems: completedRecommendedItems,
          };
        });

        const { originalProduct } = item;
        const isControlRuleActive =
          modalProps?.bannerShippingSubtitle !== BANNER_SUBTITLE_DEFAULT_TEXT;
        let controlRule =
          modalProps?.controlRuleId != null
            ? controlRulesById[modalProps?.controlRuleId ?? '']
            : {
                list_id_suffix: 'system_rule',
                list_name_prefix: 'System rule',
              };

        // TODO: refactor this so we manage rules dynamically from the controlRulesById object for each env
        if (modalProps.isBrandDailyLimitReached) {
          controlRule = {
            list_id_suffix: 'cart_item_threshold_error',
            list_name_prefix: 'cart item threshold error',
          };
        }

        const controlRuleListId =
          controlRule?.list_id_suffix != null
            ? `sim_${controlRule?.list_id_suffix}`
            : 'sim_new_control_rule';
        const controlRuleListName =
          controlRule?.list_name_prefix != null
            ? `${controlRule?.list_name_prefix} SIM`
            : 'New Control Rule SIM';
        const controlRuleItemListId =
          controlRule?.list_id_suffix != null
            ? `sim_${controlRule?.list_id_suffix}`
            : 'sim_new_control_rule';
        const controlRuleItemListName =
          controlRule?.list_name_prefix != null
            ? `${controlRule?.list_name_prefix} SIM`
            : 'New Control Rule SIM';
        const listId = isControlRuleActive ? controlRuleListId : 'sim_oos';
        const listName = isControlRuleActive
          ? controlRuleListName
          : 'Out of stock SIM';
        const itemListId = isControlRuleActive
          ? controlRuleItemListId
          : 'sim_oos';
        const itemListName = isControlRuleActive
          ? controlRuleItemListName
          : 'Out of stock SIM';

        const dataLayerRecommendationItems = [originalProduct].map(
          (recommendedItem: Record<string, any>) => {
            const formattedRecommendedItem = {
              item_id: recommendedItem.sku,
              item_name: recommendedItem.name,
              item_brand: recommendedItem.manufacturer,
              item_type: getAnalyticsItemType(recommendedItem),
              item_list_id: itemListId,
              item_list_name: itemListName,
              index: recommendationsIds.indexOf(Number(item.id)) + 1,
              quantity: 1,
              price: 0,
              item_category4: recommendedItem.industry_sector?.join(', ') || '',
              item_category5: modalProps.bagProject?.project_type_label || '',
              item_manufacturer_sample_id:
                recommendedItem.manufacturer_sample_id || '',
              family_sku: recommendedItem.family_sku || '',
            };

            return {
              ...formattedRecommendedItem,
              ...getAnalyticsItemCategoriesByTaxonomy(recommendedItem.taxonomy),
            };
          },
        );

        pushGtmDataLayer({ ecommerce: null });
        pushGtmDataLayer({
          event: 'add_to_cart',
          event_location: 'Cart recommendations',
          list_id: listId,
          list_name: listName,
          method:
            dataLayerRecommendationItems.length > 1
              ? AddToCartMethod.MULTI_SELECT
              : AddToCartMethod.SINGLE,
          ecommerce: {
            currency: 'USD',
            value: 0,
            items: dataLayerRecommendationItems,
          },
        });
      } else {
        setModalProps((prevProps) => {
          const completedRecommendedItems = prevProps.recommendedItems.map(
            (newItem) => ({
              ...newItem,
              isCartLoading:
                newItem.id === item.id ? false : newItem.isCartLoading,
              isAddedToCart:
                newItem.id === item.id ? false : newItem.isAddedToCart,
            }),
          );

          return {
            ...prevProps,
            recommendedItems: completedRecommendedItems,
          };
        });
      }
    } catch (error) {
      console.error('Cannot add to bag', error);
      if (error.message) {
        toggleSnackbarInfo({
          isOpen: true,
          otherProps: {
            message: 'Some error occurred',
            status: SnackbarStatusEnum.DANGER,
          },
        });
      }
      setModalProps((prevProps) => {
        const completedRecommendedItems = prevProps.recommendedItems.map(
          (newItem) => ({
            ...newItem,
            isCartLoading:
              newItem.id === item.id ? false : newItem.isCartLoading,
            isAddedToCart:
              newItem.id === item.id ? false : newItem.isAddedToCart,
          }),
        );

        return {
          ...prevProps,
          recommendedItems: completedRecommendedItems,
        };
      });
    }
  };

  const handleMouseOver = () => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleRecommendedItemUrlClick = (
    recommendedItem: RecommendedItemType,
  ) => {
    const { originalProduct } = recommendedItem;
    const controlRule =
      modalProps?.controlRuleId != null
        ? controlRulesById[modalProps?.controlRuleId ?? '']
        : {
            list_id_suffix: 'system_rule',
            list_name_prefix: 'System rule',
          };
    const isServerMessage =
      modalProps?.bannerShippingSubtitle !== BANNER_SUBTITLE_DEFAULT_TEXT;
    const controlRuleListId =
      controlRule?.list_id_suffix != null
        ? `sim_${controlRule?.list_id_suffix}`
        : 'sim_new_control_rule';
    const controlRuleListName =
      controlRule?.list_name_prefix != null
        ? `${controlRule?.list_name_prefix} SIM`
        : 'New Control Rule SIM';
    const controlRuleItemListId =
      controlRule?.list_id_suffix != null
        ? `sim_${controlRule?.list_id_suffix}`
        : 'sim_new_control_rule';
    const controlRuleItemListName =
      controlRule?.list_name_prefix != null
        ? `${controlRule?.list_name_prefix} SIM`
        : 'New Control Rule SIM';
    const listId = isServerMessage ? controlRuleListId : 'sim_oos';
    const listName = isServerMessage ? controlRuleListName : 'Out of stock SIM';
    const itemListId = isServerMessage ? controlRuleItemListId : 'sim_oos';
    const itemListName = isServerMessage
      ? controlRuleItemListName
      : 'Out of stock SIM';

    const dataLayerRecommendationItems = [
      {
        item_id: originalProduct.sku,
        item_name: originalProduct.name,
        item_brand: originalProduct.manufacturer,
        item_type: getAnalyticsItemType(originalProduct),
        item_list_id: itemListId,
        item_list_name: itemListName,
        index: recommendationsIds.indexOf(Number(recommendedItem.id)) + 1,
        quantity: 1,
        price: 0,
        ...getAnalyticsItemCategoriesByTaxonomy(originalProduct.taxonomy),
        item_category4: originalProduct.industry_sector?.join(', ') || '',
        item_category5: modalProps.bagProject?.project_type_label || '',
      },
    ];

    pushGtmDataLayer({ ecommerce: null });
    pushGtmDataLayer({
      event: 'select_item',
      list_id: listId,
      list_name: listName,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: dataLayerRecommendationItems,
      },
    });
    sendAnalytics(
      {
        id: [String(recommendedItem.id)],
        type: 'view',
        impressions: modalProps.recommendedItems?.map((item) =>
          String(item.id),
        ),
      },
      xHeader,
    );
  };

  return {
    handleAddToCart,
    handleMouseOver,
    handleRecommendedItemUrlClick,
  };
}
